/* Borderline winter 26/27 · the product page (P6): behaviour for sections/bl-product, bl-pdp-band, bl-pdp-looks.
   Prototype v83: winter-proto/assets/js/page-pdp.js + page-apparel.js (+ app.js stickyBar / lightbox).
   - Horizon does the selling: its hidden <variant-picker> (inside <bl-options>) fetches the page for the chosen
     options and fires 'shopify:product:select'; its price block and product form update themselves. Here every
     [data-bl-sync] part (gallery, option rows, get it, the bar, the specs and setup blocks) is morphed from that same
     fetched page, so the Liquid stays the only renderer.
   - <bl-options>: the winter option boxes press Horizon's radios; the fit finders (length, boot Mondo, helmet,
     apparel); the size chart drawer; "Select a size" before an apparel size is chosen.
   - <bl-gallery>: stage + thumbnails, the specs view, the phone swipe strip with dots, the lightbox, the heart.
   - <bl-qa>: Common questions — the short FAQ in the buy column (one answer open at a time, actions: finder,
     size chart, Get it); the full list is drawn into the Questions band from the same JSON.
   - the bar, Buy it now, Get it's store order (the header's store chip), the setup adds, the specs column heads,
     the questions' one-open rule and the lookbook's "This item" tag are wired below. */
import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig, lockScroll, unlockScroll } from '@theme/utilities';

const SELECT_EVENT = 'shopify:product:select';
const STORE_KEY = 'bl-store';
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const phone = matchMedia('(max-width: 989px)');

/** @param {Element | null | undefined} el @returns {any} */
const readJSON = (el) => {
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || 'null');
  } catch (error) {
    return null;
  }
};

const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

/**
 * A modal of ours closed: focus goes back to what opened it, unless the close itself moved focus on (the size
 * chart's "Find my size instead" focuses the finder).
 * @param {HTMLDialogElement} dialog @param {Element | null | undefined} opener
 */
const focusBack = (dialog, opener) => {
  const active = document.activeElement;
  if (active && active !== document.body && !dialog.contains(active)) return;
  if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
};

/** @param {unknown} value */
const escHTML = (value) => String(value).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);

/** The store the shopper chose in the header (P0 store chip), as { name, short }. */
const myStore = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
    return raw && typeof raw.name === 'string' ? raw : null;
  } catch (error) {
    return null;
  }
};

/** The stores as Get it lists them: [{ short, name, loc }] in display order. */
const storeList = () =>
  [...document.querySelectorAll('.bl-get__row[data-loc]')].map((row) => ({
    short: row.getAttribute('data-store') || '',
    name: row.getAttribute('data-store-name') || '',
    loc: row.getAttribute('data-loc') || '',
  }));

/** The stores that hold the selected variant today, from Get it's rows (Liquid store_availabilities, then the live
 * counts: "Last one" is a store that holds it too). */
const storesHolding = () =>
  [...document.querySelectorAll('.bl-get__row[data-loc]')]
    .filter((row) => row.querySelector('.dot--ok, .dot--low'))
    .map((row) => ({ short: row.getAttribute('data-store') || '', name: row.getAttribute('data-store-name') || '', loc: row.getAttribute('data-loc') || '' }));

/**
 * Stock words for a variant: the stores for the selected one (Liquid only knows the selected variant's stores),
 * "in stock" / "available to order" / "sold out" for the others.
 * @param {any} v - a variant from the bl-pdp-options JSON
 * @param {any} t - words
 */
const stockWords = (v, t) => {
  if (!v) return t.notMade || 'not made in this size';
  if (!v.a) return t.soldOut || 'sold out in this size';
  const known = stockKnown.get(String(v.id));
  const isCur = String(v.id) === currentVariantId() && !pageUnpicked();
  const holding = known ? known.holding : isCur ? storesHolding().map((s) => s.loc) : null;
  if (holding && holding.length) {
    const at = orderStores(storeList().filter((s) => holding.includes(s.loc)))[0];
    if (at) return `${t.inStockAt || 'in stock at'} ${at.name}`;
  }
  if (v.q === null || v.q > 0) return holding ? t.inStock || 'in stock' : t.pickToSee || t.inStock || 'in stock';
  return t.toOrder || 'available to order';
};

/* ---------- pickup stock for any variant ----------
   Live counts first (R3, 2026-10-21; the owner: straight from the stores' inventory, no synced field): Get it
   carries the product (data-handle) and the Storefront token (data-bl-sf, theme setting bl_storefront_token; never
   on a pre-order product), and assets/bl-stock.js asks the Storefront API once for every size's units per store,
   kept for the page view. That answer paints the store rows (In stock / Last one / Not here / None), the all-sizes
   rows before a size is chosen and the fit finders' "in stock at …".
   Without it (no token, an error, a product not on the Headless channel yet) Liquid's rows stand and another
   variant's stores come from one small request each (sections/bl-pdp-stock.liquid through
   /products/<handle>?variant=<id>&section_id=; the shorter /variants/<id>/ route renders sections without the
   product), kept for the page. A pickup time in days ("Usually ready in 2-4 days") is Shopify moving the piece from
   another store (ship to store), so that store counts as "not here".
   Owner 2026-10-21: pickup is the same day, during store hours, only at a store that has the piece. A store row with
   stock says the block's own words (words.pickup, "same-day pickup"), never Shopify's pickup time; a store without it
   says "Not here" with no transfer time and no pickup offer. */
const SHIP_TO_STORE = / day/i;

/** @type {Map<string, Promise<any>>} */
const stockAsked = new Map();
/** @type {Map<string, { holding: string[] }>} */
const stockKnown = new Map();

/** @type {Promise<any> | null} the live answer, asked once */
let liveAsked = null;
/** @type {any} the live answer once it came: { complete, variants: Map<id, { sale, q, at: Map<loc, { q, a, t }> }> } */
let liveStock = null;

/** Ask the Storefront API for the product's stock per store (once per page view). @returns {Promise<any>} */
function askLive() {
  if (liveAsked) return liveAsked;
  const get = document.querySelector('.bl-get[data-bl-sf][data-handle]');
  const token = get instanceof HTMLElement ? get.dataset.blSf || '' : '';
  const src = get instanceof HTMLElement ? get.dataset.blStockSrc || '' : '';
  const handle = get instanceof HTMLElement ? get.dataset.handle || '' : '';
  if (!token || !src || !handle) {
    liveAsked = Promise.resolve(null);
    return liveAsked;
  }
  liveAsked = import(src)
    .then((module) => module.productStock(handle, token))
    .then((stock) => {
      liveStock = stock || null;
      if (liveStock) {
        for (const [id, variant] of liveStock.variants) stockKnown.set(id, knownFrom(variant));
      }
      return liveStock;
    })
    .catch(() => null);
  return liveAsked;
}

/**
 * A variant's stores from the live answer, in the per-size request's shape: holding = the stores with units.
 * @param {{ at: Map<string, { q: number, a: boolean, t: string }> }} variant
 */
function knownFrom(variant) {
  /** @type {{ holding: string[] }} */
  const known = { holding: [] };
  for (const [loc, at] of variant.at) {
    if (at.q > 0) known.holding.push(loc);
  }
  return known;
}

/** @param {string | number} id @returns {Promise<any>} */
function fetchStock(id) {
  const key = String(id);
  const asked = stockAsked.get(key);
  if (asked) return asked;
  const request = askLive().then((live) => (live && stockKnown.get(key)) || fetchSectionStock(key));
  stockAsked.set(key, request);
  return request;
}

/** One variant's stores from sections/bl-pdp-stock.liquid (when the live counts cannot load). @param {string} key */
function fetchSectionStock(key) {
  const page = document.querySelector('bl-pdp');
  const base = (page instanceof HTMLElement && page.dataset.productUrl) || location.pathname;
  const url = new URL(base, location.origin);
  url.searchParams.set('variant', key);
  url.searchParams.set('section_id', 'bl-pdp-stock');
  return fetch(url.toString())
    .then((response) => (response.ok ? response.text() : ''))
    .then((text) => {
      const data = text ? readJSON(new DOMParser().parseFromString(text, 'text/html').querySelector('script[data-bl-stock]')) : null;
      if (!data || !Array.isArray(data.s)) return null;
      /** @type {{ holding: string[] }} */
      const known = { holding: [] };
      for (const entry of data.s) {
        if (!entry || !entry.a) continue;
        // a pickup time in days is ship to store (Shopify moving it from another store): not stock there
        if (!SHIP_TO_STORE.test(String(entry.t || ''))) known.holding.push(String(entry.l));
      }
      stockKnown.set(key, known);
      return known;
    })
    .catch(() => null);
}

/**
 * Get it for the chosen size from the live counts (prototype page-pdp.js cellStatus): 2 or more "In stock", exactly
 * 1 "Last one" (both "· same-day pickup", the block's words), none "Not here" (no transfer time, no pickup offer) and
 * "Ask us" on your store's row (CSS shows it there), or "None" when the size sells nowhere. Pre-order rows and the
 * all-sizes rows (no size chosen) are left alone; a row is rewritten only when its words change. The words come from
 * the block (data-bl-get-words).
 * @param {Element | null} get - .bl-get
 */
function paintLiveRows(get) {
  if (!(get instanceof HTMLElement) || !liveStock || get.dataset.preorder === 'true' || get.dataset.sizes || pageUnpicked()) return;
  const variant = liveStock.variants.get(get.dataset.variant || currentVariantId());
  const words = readJSON(get.querySelector('script[data-bl-get-words]'));
  if (!variant || !words) return;
  for (const row of get.querySelectorAll('.bl-get__row[data-loc]')) {
    const name = row.getAttribute('data-store-name') || '';
    const at = variant.at.get(row.getAttribute('data-loc') || '');
    const q = at ? at.q : 0;
    let dot = 'none';
    let status = words.none || '';
    let strong = '';
    let ask = '';
    if (q > 0) {
      dot = q === 1 ? 'low' : 'ok';
      status = (q === 1 && words.lastOne) || words.inStock || '';
      strong = words.pickup || '';
    } else if (variant.sale) {
      dot = 'xfer';
      status = words.notHere || '';
      ask = words.size
        ? String(words.askItem || '').replace('[item]', words.size).replace('[store]', name)
        : String(words.askAny || '').replace('[store]', name);
    }
    const cell = row.querySelector('.bl-get__s');
    const wordsNow = `${status}${strong ? ` · ${strong}` : ''}`;
    const said = cell ? (cell.textContent || '').replace(/\s+/g, ' ').trim() : '';
    if (cell && (said !== wordsNow || !cell.querySelector(`.dot--${dot}`))) {
      cell.innerHTML = `<i class="dot dot--${dot}" aria-hidden="true"></i>${escHTML(status)}${strong ? ` · <b>${escHTML(strong)}</b>` : ''}`;
    }
    const right = row.querySelector('.bl-get__r');
    const askHTML = ask && words.email ? `<a class="text-link bl-get__ask" href="mailto:${escHTML(words.email)}?subject=${encodeURIComponent(ask)}">${escHTML(words.ask || 'Ask us')} →</a>` : '';
    if (right && !!right.querySelector('.bl-get__ask') !== !!askHTML) right.innerHTML = askHTML;
    row.classList.toggle('is-none', dot === 'none');
  }
  get.dataset.live = 'done';
}

/**
 * R15 · the specs band's stock (snippets/bl-specs-band, data-bl-stock-v): the key figure's note under Length says
 * "2 in stock at Richmond Hill" for the shopper's store (Get it's order: the header's store first), nothing at 0;
 * the All lengths table's Stock cells say "2 · RH" (the shopper's store, else the first store holding it) or "–".
 * Live counts first; without them, the per-size stores (no counts: "In stock at …" / "RH").
 */
function paintSpecStock() {
  const band = document.querySelector('.bl-sp2[data-w-stock]');
  if (!(band instanceof HTMLElement)) return;
  const stores = orderStores(storeList());
  if (!stores.length) return;
  const mine = stores[0];
  const tpl = band.dataset.wStock || '';
  const tplAny = band.dataset.wStockAny || '';
  for (const el of band.querySelectorAll('[data-bl-stock-v]')) {
    if (!(el instanceof HTMLElement)) continue;
    const id = el.dataset.blStockV || '';
    const note = el.dataset.blStockKind === 'note';
    fetchStock(id).then((/** @type {any} */ known) => {
      if (!el.isConnected || el.dataset.blStockV !== id) return;
      const live = liveStock ? liveStock.variants.get(id) : null;
      let text = '';
      if (live) {
        const qAt = (/** @type {string} */ loc) => {
          const at = live.at.get(loc);
          return at ? at.q : 0;
        };
        const q = qAt(mine.loc);
        if (note) text = q > 0 ? tpl.replace('[n]', String(q)).replace('[store]', mine.name) : '';
        else {
          const holder = q > 0 ? mine : stores.find((st) => qAt(st.loc) > 0);
          text = holder ? `${qAt(holder.loc)} · ${holder.short}` : '–';
        }
      } else if (known && Array.isArray(known.holding)) {
        const holding = stores.filter((st) => known.holding.includes(st.loc));
        if (note) text = holding.includes(mine) ? tplAny.replace('[store]', mine.name) : '';
        else text = holding.length ? holding.map((st) => st.short).join(' · ') : '–';
      }
      if (el.textContent !== text) el.textContent = text;
    });
  }
}

/** True while the page waits for a size (apparel). */
const pageUnpicked = () => {
  const root = document.querySelector('bl-pdp');
  return root instanceof HTMLElement && root.dataset.unpicked === 'true';
};

/**
 * Sizes as a run in the product's order: XS–M · XL (three or more in a row become a range; prototype sizeRun).
 * @param {string[]} list @param {string[]} order
 */
function sizeRun(list, order) {
  const idx = [...new Set(list.map((size) => order.indexOf(size)).filter((k) => k >= 0))].sort((a, b) => a - b);
  /** @type {string[]} */
  const out = [];
  let i = 0;
  while (i < idx.length) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1] === idx[j] + 1) j++;
    out.push(j - i >= 2 ? `${order[idx[i]]}–${order[idx[j]]}` : idx.slice(i, j + 1).map((k) => order[k]).join(' · '));
    i = j + 1;
  }
  return out.join(' · ');
}

/** The page's variants (bl-pdp-options JSON). */
const variantData = () => readJSON(document.querySelector('script[data-bl-variants]'));

/** The variant the product form will add (Horizon keeps input[name=id] current). */
const currentVariantId = () => {
  const input = document.querySelector('.bl-cta product-form-component input[name="id"], .bl-pdp-root form[data-type="add-to-cart-form"] input[name="id"]');
  return input instanceof HTMLInputElement ? input.value : '';
};

/* ---------- re-render every [data-bl-sync] part from the page Horizon fetched ---------- */

/** @param {Document} html */
function syncFrom(html) {
  for (const el of document.querySelectorAll('[data-bl-sync]')) {
    const key = el.getAttribute('data-bl-sync') || '';
    const next = html.querySelector(`[data-bl-sync="${CSS.escape(key)}"]`);
    if (!next) continue;
    try {
      morph(el, next, { ...MORPH_OPTIONS, childrenOnly: false });
    } catch (error) {
      console.warn('[bl-pdp] sync failed for', key, error);
    }
  }
  document.dispatchEvent(new CustomEvent('bl:pdp:synced'));
}

/* A second option chosen while Horizon still fetches the first aborts that fetch; Horizon rejects the select event's
   promise with an AbortError that a platform listener leaves unhandled. It is expected, not a failure: keep it out
   of the console (only AbortError). */
window.addEventListener('unhandledrejection', (event) => {
  const reason = /** @type {any} */ (event).reason;
  if (reason && reason.name === 'AbortError') event.preventDefault();
});

document.addEventListener(SELECT_EVENT, (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('product-card, quick-add-dialog, bl-product-card, bl-lookbook-component')) return;
  if (!target.closest('bl-pdp')) return;
  const promise = /** @type {any} */ (event).promise;
  if (!promise) return;
  promise
    .then((/** @type {any} */ result) => {
      const detail = result && result.detail;
      if (!detail || !detail.html) return;
      syncFrom(detail.html);
      document.dispatchEvent(new CustomEvent('bl:pdp:variant', { detail: { variant: detail.resource || null } }));
    })
    .catch((/** @type {any} */ error) => {
      if (error && error.name !== 'AbortError') console.warn('[bl-pdp] variant fetch failed', error);
    });
});

/* ---------- cart adds (the setup rows): /cart/add.js + the theme's cart event, as P0's card ---------- */

/**
 * @param {Array<{ id: number | string, quantity: number }>} items
 * @param {Element} source
 * @returns {Promise<boolean>}
 */
async function addToCart(items, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
  const body = JSON.stringify({ items, ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}) });
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const config = fetchConfig('json', { body });
    const response = await fetch(Theme.routes.cart_add_url, config);
    const result = await response.json();
    const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
    const detail = { items: cart.items, source: 'bl-pdp', sourceId: 'bl-pdp-setup', itemCount: items.length };
    if (result.status) {
      source.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID', detail: { description: result.description } }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, didError: true } });
      return false;
    }
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, sections: result.sections, didError: false } });
    return true;
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: error instanceof Error ? error.message : 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    return false;
  }
}

/* ---------- the root: unpicked state, the bar, Buy it now, Get it's store order ---------- */

class BlPdp extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  #ctaOut = false;
  /** phones (< 768): the buy buttons are still below the window (the bar shows from the page's load until they come in) */
  #ctaBelow = false;
  #footerIn = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener('bl:pdp:synced', () => this.#afterSync(), { signal });
    document.addEventListener('bl:pdp:variant', (event) => this.#onVariant(/** @type {CustomEvent} */ (event)), { signal });
    document.addEventListener('bl:store', () => {
      this.paintStores();
      paintSpecStock();
    }, { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.paintStores();
    this.#syncCta();
    this.#watchBar();
    this.#fillSizes();
    askLive().then(() => {
      if (!signal.aborted) this.#paintLive();
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#io?.disconnect();
  }

  get unpicked() {
    return this.dataset.unpicked === 'true';
  }

  /** The shopper chose a size: the buttons come back. */
  pick() {
    if (!this.unpicked) return;
    this.dataset.unpicked = 'false';
    for (const el of this.querySelectorAll('[data-unpicked]')) el.setAttribute('data-unpicked', 'false');
  }

  /** @type {AbortController | null} */
  #picked = null;

  /**
   * Horizon fetches the page with ?option_values=, which the server reads as "no size chosen yet"; on a page where the
   * size must be chosen, re-render this section once more for ?variant= so Get it, the option rows and the bar show
   * the chosen size.
   * @param {CustomEvent} event
   */
  #onVariant(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant || this.dataset.requirePick !== 'true' || this.dataset.unpicked !== 'false') return;
    for (const el of this.querySelectorAll('[data-unpicked="true"]')) el.setAttribute('data-unpicked', 'false');
    const id = variant.id;
    const sectionId = this.dataset.sectionId;
    if (!id || !sectionId) return;
    this.#picked?.abort();
    this.#picked = new AbortController();
    const url = new URL(location.href);
    url.search = '';
    url.searchParams.set('variant', String(id));
    url.searchParams.set('section_id', sectionId);
    const view = new URL(location.href).searchParams.get('view');
    if (view) url.searchParams.set('view', view);
    fetch(url.toString(), { signal: this.#picked.signal })
      .then((response) => response.text())
      .then((text) => {
        if (this.dataset.unpicked !== 'false') return;
        syncFrom(new DOMParser().parseFromString(text, 'text/html'));
      })
      .catch((error) => {
        if (error && error.name !== 'AbortError') console.warn('[bl-pdp] section refresh failed', error);
      });
  }

  #afterSync() {
    if (this.dataset.requirePick === 'true' && this.dataset.unpicked === 'false') {
      for (const el of this.querySelectorAll('[data-unpicked="true"]')) el.setAttribute('data-unpicked', 'false');
    }
    if (this.dataset.requirePick === 'true' && this.dataset.unpicked === 'true') this.#keepUnpicked();
    this.paintStores();
    this.#syncCta();
    this.#applyBar();
    this.#fillSizes();
    // the rows came back from Liquid for the new size: the live counts again, from the answer already here
    this.#paintLive();
  }

  /** Get it's store rows from the live counts (once they came; again after every re-render); the specs band's stock. */
  #paintLive() {
    paintLiveRows(this.querySelector('.bl-get'));
    paintSpecStock();
  }

  /**
   * Shopify renders an option_values fetch for a whole variant (Horizon's hidden picker always holds a size), so a
   * colour chosen before a size comes back "picked": Get it goes back to "all sizes" for the new colour and the bar to
   * "Choose a size" (the size boxes are cleared by bl-options).
   */
  #keepUnpicked() {
    const get = this.querySelector('.bl-get');
    if (get instanceof HTMLElement && !get.dataset.sizes) {
      const words = readJSON(get.querySelector('script[data-bl-get-words]'));
      if (words) {
        const eyebrow = get.querySelector('.bl-get__eyebrow');
        if (eyebrow && words.allSizes) eyebrow.textContent = words.allSizes;
        /* pre-order: every size of every store already says "Pre-order · ships <date>" (blocks/bl-pdp-getit), so the
           rows stay; only the eyebrow goes back to "all sizes" */
        if (get.dataset.preorder !== 'true') {
          for (const row of get.querySelectorAll('.bl-get__row[data-loc]')) {
            row.classList.remove('is-none');
            const cell = row.querySelector('.bl-get__s');
            if (cell) cell.innerHTML = `<i class="dot dot--wait" aria-hidden="true"></i>${escHTML(words.checking || '')}`;
            const right = row.querySelector('.bl-get__r');
            if (right) right.innerHTML = '';
          }
          get.dataset.sizes = 'pending';
        }
      }
    }
    const bar = this.querySelector('.bl-mbar');
    if (bar instanceof HTMLElement && bar.dataset.unpicked !== 'true') {
      bar.dataset.unpicked = 'true';
      for (const line of bar.querySelectorAll('.bl-mbar__s .s--l, .bl-mbar__s .s--s')) line.textContent = bar.dataset.choose || '';
      const button = bar.querySelector('button');
      if (button && !button.hasAttribute('data-bl-pick')) {
        const pick = document.createElement('button');
        pick.type = 'button';
        pick.className = 'btn btn--primary';
        pick.setAttribute('data-bl-pick', '');
        pick.tabIndex = -1;
        pick.textContent = bar.dataset.select || '';
        button.replaceWith(pick);
      }
    }
  }

  /** Get it before a size is chosen: the sizes each store holds in this colour (prototype sizesAt / sizeRun). */
  async #fillSizes() {
    const get = this.querySelector('.bl-get[data-sizes="pending"]');
    if (!(get instanceof HTMLElement)) return;
    const data = variantData();
    const words = readJSON(get.querySelector('script[data-bl-get-words]')) || {};
    const ci = Number(get.dataset.colourIx);
    const si = Number(get.dataset.sizeIx);
    const colour = get.dataset.colour || '';
    if (!data || !Array.isArray(data.v) || !(si >= 0)) return;
    const inColour = data.v.filter((/** @type {any} */ v) => !(ci >= 0) || v.o[ci] === colour);
    const order = [...new Set(inColour.map((/** @type {any} */ v) => String(v.o[si])))];
    const selling = inColour.filter((/** @type {any} */ v) => v.a);
    const results = await Promise.all(selling.map((/** @type {any} */ v) => fetchStock(v.id).then((known) => ({ v, known }))));
    // a colour switch re-rendered the rows meanwhile: that render fills itself
    if (!get.isConnected || get.dataset.sizes !== 'pending' || (get.dataset.colour || '') !== colour) return;
    const failed = !results.some((r) => r.known);
    for (const row of get.querySelectorAll('.bl-get__row[data-loc]')) {
      const loc = row.getAttribute('data-loc') || '';
      const name = row.getAttribute('data-store-name') || '';
      /** @type {string[]} */
      const sizes = [];
      for (const { v, known } of results) {
        if (known && known.holding.includes(loc)) sizes.push(String(v.o[si]));
      }
      // not at this store: "Not here", no transfer time and no pickup offer
      let dot = 'xfer';
      let status = words.notHere || 'Not here';
      let strong = '';
      let ask = String(words.askAny || '').replace('[store]', name);
      if (failed) {
        dot = 'wait';
        status = words.choose || '';
        strong = '';
        ask = '';
      } else if (sizes.length) {
        dot = 'ok';
        status = sizeRun(sizes, order);
        strong = words.pickup || '';
        ask = '';
      }
      const cell = row.querySelector('.bl-get__s');
      if (cell) cell.innerHTML = `<i class="dot dot--${dot}" aria-hidden="true"></i>${escHTML(status)}${strong ? ` · <b>${escHTML(strong)}</b>` : ''}`;
      const right = row.querySelector('.bl-get__r');
      if (right) right.innerHTML = ask && words.email ? `<a class="text-link bl-get__ask" href="mailto:${escHTML(words.email)}?subject=${encodeURIComponent(ask)}">${escHTML(words.ask || 'Ask us')} →</a>` : '';
    }
    get.dataset.sizes = 'done';
  }

  /** Buy it now and the price in the button follow the variant (cta-meta is re-rendered with the page). */
  #syncCta() {
    for (const cta of this.querySelectorAll('.bl-cta')) {
      const meta = cta.querySelector('.bl-cta__meta');
      if (!(meta instanceof HTMLElement) || !(cta instanceof HTMLElement)) continue;
      const available = meta.dataset.available === 'true';
      cta.dataset.available = String(available);
      const price = meta.dataset.price || '';
      cta.style.setProperty('--bl-cta-price', JSON.stringify(price));
      const now = cta.querySelector('[data-bl-buynow]');
      if (now instanceof HTMLAnchorElement && meta.dataset.id) {
        const url = new URL(now.href, location.origin);
        url.pathname = url.pathname.replace(/\/cart\/[^/]*$/, `/cart/${meta.dataset.id}:1`);
        now.href = url.toString();
        now.hidden = !available;
      }
    }
  }

  /** Get it: the chosen store first, tagged "your store"; its row offers "Ask us". */
  paintStores() {
    const chosen = myStore();
    for (const rows of this.querySelectorAll('.bl-get__rows')) {
      const storeRows = [...rows.querySelectorAll('.bl-get__row[data-store]')];
      if (!storeRows.length) continue;
      let mine = storeRows[0];
      if (chosen) {
        const hit = storeRows.find(
          (row) => row.getAttribute('data-store') === chosen.short || row.getAttribute('data-store-name') === chosen.name
        );
        if (hit) mine = hit;
      }
      if (mine && mine !== rows.firstElementChild) rows.prepend(mine);
      for (const row of storeRows) row.classList.toggle('is-mine', row === mine);
    }
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const pick = target.closest('[data-bl-pick]');
    if (pick) {
      event.preventDefault();
      this.flashSize();
      return;
    }
    const add = target.closest('[data-bl-bar-add]');
    if (add) {
      const main = this.querySelector('.bl-cta product-form-component [ref="addToCartButton"], .bl-cta .add-to-cart-button');
      if (main instanceof HTMLButtonElement && !main.disabled) main.click();
      return;
    }
  }

  /** Scroll to the size row and flash it (prototype: "Select a size"). */
  flashSize() {
    const opts = this.querySelector('bl-options');
    const pos = opts instanceof HTMLElement ? opts.dataset.sizePos : '';
    const row = (pos && this.querySelector(`.bl-opt[data-pos="${pos}"]`)) || this.querySelector('.bl-opt');
    if (!(row instanceof HTMLElement)) return;
    row.scrollIntoView({ behavior: behavior(), block: 'center' });
    row.classList.remove('is-flash');
    void row.offsetWidth;
    row.classList.add('is-flash');
    const first = row.querySelector('.opt:not(.is-out)') || row.querySelector('.opt');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }

  /* the bar: shown once the buy buttons are above the window, hidden over the footer and while a dialog is open.
     Phones (R13 #4, owner 10/27): also from the page's load until the buy buttons come into the window — the first
     screen then has the price and the button — so a phone sees it whenever the buttons are out of view */
  #watchBar() {
    const bar = this.querySelector('.bl-mbar');
    const cta = this.querySelector('.bl-cta');
    if (!bar || !cta || !('IntersectionObserver' in window)) return;
    const phone = matchMedia('(max-width: 767px)');
    /* the buttons count as in view once their top clears the bar (its 64 px + the safe area) */
    const below = () => phone.matches && cta.getBoundingClientRect().top >= innerHeight - bar.offsetHeight;
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === cta) {
            this.#ctaOut = !entry.isIntersecting && entry.boundingClientRect.bottom < 80;
            this.#ctaBelow = below();
          } else this.#footerIn = entry.isIntersecting;
        }
        this.#applyBar();
      },
      { threshold: 0, rootMargin: '-64px 0px 0px 0px' }
    );
    this.#io.observe(cta);
    const footer = document.querySelector('.ftr, footer, [class*="footer-group"]');
    if (footer) this.#io.observe(footer);
    // the observer only reports edge crossings: a one-step jump from below the buttons to above them (an in-page
    // link, scroll restoration) crosses none, so a frame-throttled scroll check reads the same edge (64px)
    let frame = 0;
    const check = () => {
      frame = 0;
      const out = cta.getBoundingClientRect().bottom <= 64;
      const low = below();
      if (out === this.#ctaOut && low === this.#ctaBelow) return;
      this.#ctaOut = out;
      this.#ctaBelow = low;
      this.#applyBar();
    };
    const queue = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    const signal = this.#controller?.signal;
    window.addEventListener('scroll', queue, { passive: true, signal });
    window.addEventListener('resize', queue, { passive: true, signal });
    phone.addEventListener('change', queue, { signal });
    queue();
  }

  #applyBar() {
    const bar = this.querySelector('.bl-mbar');
    if (!(bar instanceof HTMLElement)) return;
    const dialogOpen = !!document.querySelector('dialog[open]:not(.bl-quiet)');
    const on = (this.#ctaOut || this.#ctaBelow) && !this.#footerIn && !dialogOpen;
    bar.classList.toggle('is-on', on);
    bar.setAttribute('aria-hidden', String(!on));
    for (const button of bar.querySelectorAll('button')) button.tabIndex = on ? 0 : -1;
  }
}

/* ---------- options: the boxes, the finders, the size chart ---------- */

/** The sizing rules, shared with the Find my … pages (assets/bl-finder.js, loaded before this file). */
const FIT = () => /** @type {any} */ (window).BLFit;

class BlOptions extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {any} */
  #data = null;
  #open = false;
  /** finder inputs survive re-renders */
  #state = { kg: '', style: 0, head: '', sys: 0, shoe: '', h: '', layer: 0 };
  /** @type {string | null} the recommended option value */
  #rec = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#data = readJSON(this.querySelector('script[data-bl-variants]')) || { v: [], t: {} };
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('input', (event) => this.#onInput(event), { signal });
    this.addEventListener('change', (event) => this.#onInput(event), { signal });
    document.addEventListener('bl:pdp:synced', () => this.#afterSync(), { signal });
    const dialog = this.querySelector('dialog.bl-drawer');
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      }, { signal });
      /* every way out (×, the backdrop, Escape, "Find my size instead"): the page scrolls again, focus goes back */
      dialog.addEventListener('close', () => {
        unlockScroll(dialog);
        focusBack(dialog, this.querySelector('[data-bl-sizechart]'));
      }, { signal });
    }
    if (location.hash === '#find' || location.hash === '#ask') {
      requestAnimationFrame(() => this.toggleFinder(true, true));
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    const dialog = this.querySelector('dialog.bl-drawer');
    if (dialog) unlockScroll(dialog);
  }

  get t() {
    return (this.#data && this.#data.t) || {};
  }

  get root() {
    return this.closest('bl-pdp');
  }

  /** The size option's values in order. */
  get sizeValues() {
    const pos = Number(this.dataset.sizePos || 0);
    if (!pos) return [];
    const row = this.querySelector(`.bl-opt[data-pos="${pos}"] .bl-opt__vals`);
    return row ? [...row.querySelectorAll('.opt')].map((b) => /** @type {HTMLElement} */ (b).dataset.value || '') : [];
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const box = target.closest('.bl-opt__vals .opt');
    if (box instanceof HTMLElement) {
      this.choose(Number(box.dataset.pos), box.dataset.valueId || '', box);
      return;
    }
    if (target.closest('[data-bl-finder]')) {
      this.toggleFinder(!this.#open);
      return;
    }
    const style = target.closest('[data-f-style]');
    if (style instanceof HTMLElement) {
      this.#state.style = Number(style.dataset.fStyle);
      this.#paintFinder(true);
      return;
    }
    const layer = target.closest('[data-f-layer]');
    if (layer instanceof HTMLElement) {
      this.#state.layer = Number(layer.dataset.fLayer);
      this.#paintFinder(true);
      return;
    }
    const sel = target.closest('[data-f-select]');
    if (sel instanceof HTMLElement) {
      this.chooseValue(sel.dataset.fSelect || '');
      return;
    }
    if (target.closest('[data-bl-sizechart]')) {
      const dialog = this.querySelector('dialog.bl-drawer');
      if (dialog instanceof HTMLDialogElement && !dialog.open) {
        lockScroll(dialog);
        dialog.showModal();
      }
      return;
    }
    if (target.closest('[data-bl-close]')) {
      const dialog = target.closest('dialog');
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }
    if (target.closest('[data-bl-sc-find]')) {
      const dialog = this.querySelector('dialog.bl-drawer');
      if (dialog instanceof HTMLDialogElement) dialog.close();
      this.toggleFinder(true, true);
    }
  }

  /** @param {Event} event */
  #onInput(event) {
    const el = event.target;
    if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
    const key = el.dataset.f;
    if (!key) return;
    const next = key === 'sys' ? Number(el.value) : el.value;
    /* the 'change' a field fires when it loses focus repeats its last 'input': repainting then would swap the
       Select button under a press that is landing on it (mousedown blurs the field), and the click would be lost */
    if (/** @type {any} */ (this.#state)[key] === next) return;
    /** @type {any} */ (this.#state)[key] = next;
    this.#paintFinder(false);
  }

  /**
   * Press an option value: Horizon's hidden radio does the fetch; the boxes answer at once.
   * @param {number} pos - option position (1-based)
   * @param {string} valueId - Shopify option value id
   * @param {HTMLElement} [box]
   */
  choose(pos, valueId, box) {
    if (!pos || !valueId) return;
    const sizePos = Number(this.dataset.sizePos || 0);
    const firstPick = pos === sizePos && this.root instanceof BlPdp && this.root.unpicked;
    if (pos === sizePos && this.root instanceof BlPdp) this.root.pick();
    if (box) {
      const group = box.closest('.bl-opt__vals');
      for (const b of group ? group.querySelectorAll('.opt') : []) {
        b.classList.toggle('is-on', b === box);
        b.setAttribute('aria-pressed', String(b === box));
      }
      const lbl = this.querySelector(`.bl-opt[data-pos="${pos}"] .bl-opt__lbl`);
      if (lbl) {
        let value = lbl.querySelector('b');
        if (!value) {
          value = document.createElement('b');
          lbl.append(value);
        }
        value.textContent = box.dataset.value || '';
      }
    }
    const radio = this.querySelector(`variant-picker input[data-option-value-id="${CSS.escape(valueId)}"]`);
    if (radio instanceof HTMLInputElement) {
      if (radio.checked && box && box.classList.contains('is-on') && !firstPick && !this.#wasUnpicked(pos)) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const select = this.querySelector('variant-picker select');
    const option = select ? select.querySelector(`option[data-option-value-id="${CSS.escape(valueId)}"]`) : null;
    if (select instanceof HTMLSelectElement && option instanceof HTMLOptionElement) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    /* no Horizon control for this value (single-value option): go to the variant's URL */
    const match = this.#variantFor(pos, box ? box.dataset.value || '' : '');
    if (match) location.assign(`${location.pathname}?variant=${match.id}`);
  }

  /** @param {number} pos */
  #wasUnpicked(pos) {
    return pos === Number(this.dataset.sizePos || 0) && this.dataset.unpicked === 'true';
  }

  /**
   * The variant with this value at pos and the current values elsewhere.
   * @param {number} pos @param {string} value
   */
  #variantFor(pos, value) {
    const list = (this.#data && this.#data.v) || [];
    const curId = currentVariantId();
    const cur = list.find((/** @type {any} */ v) => String(v.id) === curId) || list[0];
    return (
      list.find((/** @type {any} */ v) => v.o[pos - 1] === value && v.o.every((/** @type {string} */ o, /** @type {number} */ i) => i === pos - 1 || !cur || o === cur.o[i])) ||
      list.find((/** @type {any} */ v) => v.o[pos - 1] === value) ||
      null
    );
  }

  /** Select a value of the size option by its text (finder, specs table, ask box). @param {string} value */
  chooseValue(value) {
    const pos = Number(this.dataset.sizePos || 0);
    this.chooseAt(pos, value);
  }

  /** @param {number} pos @param {string} value */
  chooseAt(pos, value) {
    if (!pos) return;
    const box = [...this.querySelectorAll(`.bl-opt[data-pos="${pos}"] .opt`)].find((b) => /** @type {HTMLElement} */ (b).dataset.value === value);
    if (box instanceof HTMLElement) this.choose(pos, box.dataset.valueId || '', box);
  }

  #afterSync() {
    /* keep the finder's mark and the chosen size on the re-rendered boxes */
    if (this.dataset.unpicked === 'true' && this.root instanceof BlPdp && !this.root.unpicked) {
      this.dataset.unpicked = 'false';
    }
    if (this.root instanceof BlPdp && this.root.unpicked) {
      const pos = Number(this.dataset.sizePos || 0);
      for (const box of this.querySelectorAll(`.bl-opt[data-pos="${pos}"] .bl-opt__vals .opt.is-on`)) {
        box.classList.remove('is-on');
        box.setAttribute('aria-pressed', 'false');
      }
      const chosen = this.querySelector(`.bl-opt[data-pos="${pos}"] .bl-opt__lbl b`);
      if (chosen) chosen.remove();
    }
    this.#markRec();
    if (this.#open) this.#paintFinder(false);
  }

  /* ---------- the finders ---------- */

  /** @param {boolean} open @param {boolean} [scroll] */
  toggleFinder(open, scroll) {
    const panel = this.querySelector('[data-bl-finder-panel]');
    const button = this.querySelector('[data-bl-finder]');
    if (!(panel instanceof HTMLElement) || !(button instanceof HTMLElement)) return;
    this.#open = open;
    panel.hidden = !open;
    button.setAttribute('aria-expanded', String(open));
    button.textContent = open ? button.dataset.hide || 'Hide' : button.dataset.label || '';
    if (open) {
      this.#paintFinder(true);
      if (scroll) panel.scrollIntoView({ behavior: behavior(), block: 'center' });
      const first = panel.querySelector('input, select');
      if (first instanceof HTMLElement) first.focus({ preventScroll: true });
    } else {
      this.#rec = null;
      this.#markRec();
    }
  }

  /** @param {boolean} rebuild - rebuild the fields (false: only the result line, so typing keeps focus) */
  #paintFinder(rebuild) {
    const panel = this.querySelector('[data-bl-finder-panel]');
    if (!(panel instanceof HTMLElement)) return;
    const kind = this.dataset.finder || '';
    const t = this.t;
    const st = this.#state;
    const esc = (/** @type {string} */ s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);
    if (rebuild || !panel.firstElementChild) {
      let fields = '';
      if (kind === 'length') {
        const styles = t.styles || ['All-mountain', 'Freestyle', 'Powder'];
        fields = `<div class="bl-finder__row"><label class="bl-finder__f"><span class="bl-lbl">${esc(t.weight || 'Weight (kg)')}</span><input class="bl-finder__in" type="number" inputmode="numeric" min="25" max="140" placeholder="70" value="${esc(st.kg)}" data-f="kg"></label><div class="bl-finder__f"><span class="bl-lbl">${esc(t.style || 'How you ride')}</span><div class="bl-finder__chips">${styles.map((s, i) => `<button type="button" class="pill${i === st.style ? ' is-on' : ''}" aria-pressed="${i === st.style}" data-f-style="${i}">${esc(s)}</button>`).join('')}</div></div></div>`;
      } else if (kind === 'helmet') {
        fields = `<label class="bl-finder__f"><span class="bl-lbl">${esc(t.head || 'Head circumference (cm)')}</span><input class="bl-finder__in" type="number" inputmode="decimal" min="44" max="68" step="0.5" placeholder="57" value="${esc(st.head)}" data-f="head"></label>`;
      } else if (kind === 'boot') {
        const systems = t.systems || ['Foot length (cm)', 'US men’s', 'US women’s', 'EU'];
        fields = `<div class="bl-finder__row bl-finder__row--sys"><label class="bl-finder__f"><span class="bl-lbl">${esc(t.shoe || 'Your size')}</span><select class="bl-finder__sel" data-f="sys">${systems.map((s, i) => `<option value="${i}"${i === st.sys ? ' selected' : ''}>${esc(s)}</option>`).join('')}</select></label><label class="bl-finder__f"><span class="bl-lbl">&nbsp;</span><input class="bl-finder__in" type="number" inputmode="decimal" step="0.5" placeholder="${[26.5, 9, 9, 42][st.sys] || 9}" value="${esc(st.shoe)}" data-f="shoe" aria-label="${esc(systems[st.sys] || '')}"></label></div>`;
      } else if (kind === 'apparel') {
        const layers = t.layers || ['Base layer', 'Fleece under'];
        fields = `<div class="bl-finder__row bl-finder__row--3"><label class="bl-finder__f"><span class="bl-lbl">${esc(t.height || 'Height (cm)')}</span><input class="bl-finder__in" type="number" inputmode="numeric" min="120" max="210" placeholder="170" value="${esc(st.h)}" data-f="h"></label><label class="bl-finder__f"><span class="bl-lbl">${esc(t.weight || 'Weight (kg)')}</span><input class="bl-finder__in" type="number" inputmode="numeric" min="30" max="140" placeholder="62" value="${esc(st.kg)}" data-f="kg"></label><div class="bl-finder__f"><span class="bl-lbl">${esc(t.layer || 'How you layer')}</span><div class="bl-finder__chips">${layers.map((s, i) => `<button type="button" class="pill${i === st.layer ? ' is-on' : ''}" aria-pressed="${i === st.layer}" data-f-layer="${i}">${esc(s)}</button>`).join('')}</div></div></div>`;
      }
      panel.innerHTML = `<div class="bl-finder">${fields}<div class="bl-finder__out" data-f-out aria-live="polite"></div></div>`;
    }
    const out = panel.querySelector('[data-f-out]');
    if (!(out instanceof HTMLElement)) return;
    const result = this.#recommend(kind);
    this.#rec = result ? result.value : null;
    this.#markRec();
    if (!result) {
      out.classList.remove('is-got');
      this.#setOut(out, `<span>${esc(kind === 'helmet' ? t.headHint || '' : kind === 'boot' ? t.shoeHint || '' : kind === 'apparel' ? t.enterHW || '' : t.enterWeight || '')}</span>`);
      this.#pageLink(panel);
      return;
    }
    out.classList.add('is-got');
    const label = result.value;
    let html = `<span>→ ${esc(result.lead ? result.lead + ' · ' : '')}${esc(label)} · ${esc(result.stock)}${result.extra ? ' · ' + esc(result.extra) : ''}</span>${result.made ? `<button type="button" class="text-link" data-f-select="${esc(result.value)}">${esc(t.select || 'Select')} ${esc(label)}</button>` : ''}`;
    if (kind === 'helmet' && t.headTip) html += `<p class="bl-finder__tip">${esc(t.headTip)}</p>`;
    this.#setOut(out, html);
    this.#pageLink(panel);
  }

  /** @type {WeakMap<Element, string>} the result line as last written */
  #outHtml = new WeakMap();

  /**
   * Write the result line only when it says something new: a rewrite replaces the Select button, and a press
   * already on its way to the old one (a re-render after a variant fetch) would be lost.
   * @param {HTMLElement} out @param {string} html
   */
  #setOut(out, html) {
    if (this.#outHtml.get(out) === html) return;
    this.#outHtml.set(out, html);
    out.innerHTML = html;
  }

  /** The full Find my … page under the finder (length / boot / helmet). @param {HTMLElement} panel */
  #pageLink(panel) {
    const t = this.t;
    if (!t.page || panel.querySelector('[data-bl-finder-page]')) return;
    const a = document.createElement('a');
    a.className = 'text-link bl-finder__page';
    a.href = t.page;
    a.setAttribute('data-bl-finder-page', '');
    a.textContent = `${t.pageLabel || 'Open the full fit tool'} →`;
    panel.querySelector('.bl-finder')?.append(a);
  }

  /** @param {string} kind */
  #recommend(kind) {
    const values = this.sizeValues;
    if (!values.length) return null;
    const st = this.#state;
    let value = null;
    let lead = '';
    let extra = '';
    if (kind === 'length') {
      const kg = parseFloat(st.kg);
      if (!kg) return null;
      value = this.#recommendLength(kg, st.style, values);
    } else if (kind === 'helmet') {
      const cm = parseFloat(st.head);
      if (!cm) return null;
      value = FIT().recommendHelmet(cm, values);
    } else if (kind === 'boot') {
      const n = parseFloat(st.shoe);
      if (!n) return null;
      const mondo = FIT().toMondo(n, st.sys);
      if (!mondo) return null;
      lead = `${this.t.mondo || 'Mondo'} ${mondo}`;
      value = FIT().recommendBoot(mondo, values);
      extra = this.t.fitter || '';
    } else if (kind === 'apparel') {
      const h = parseFloat(st.h);
      const kg = parseFloat(st.kg);
      if (!h || !kg) return null;
      value = FIT().recommendApparel(h, kg, st.layer === 1, values);
      if (value && st.layer === 1) extra = this.t.fleeceNote || '';
    }
    if (!value) return null;
    return { value, lead, extra, made: values.includes(value), stock: this.#stockLine(value) };
  }

  /**
   * Length from weight and riding style: the variants' rider-weight ranges when the product has them, else the
   * prototype's rule (content.js recommendLength).
   * @param {number} kg @param {number} style 0 all-mountain, 1 freestyle, 2 powder @param {string[]} values
   */
  #recommendLength(kg, style, values) {
    const sorted = values.slice().sort((a, b) => parseFloat(a) - parseFloat(b));
    const pos = Number(this.dataset.sizePos || 0);
    const list = (this.#data && this.#data.v) || [];
    const ranges = sorted.map((value) => {
      const v = list.find((/** @type {any} */ x) => x.o[pos - 1] === value && x.rw);
      const m = v ? String(v.rw).match(/(\d+(?:\.\d+)?)\s*[–—-]\s*(\d+(?:\.\d+)?)/) : null;
      return m ? [parseFloat(m[1]), parseFloat(m[2])] : null;
    });
    let i;
    if (ranges.filter(Boolean).length >= Math.max(2, Math.ceil(sorted.length / 2))) {
      let best = -1;
      let bestD = Infinity;
      ranges.forEach((r, k) => {
        if (!r) return;
        const mid = (r[0] + r[1]) / 2;
        const inside = kg >= r[0] && kg <= r[1];
        const d = Math.abs(kg - mid) + (inside ? 0 : 1000);
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      });
      i = best < 0 ? 0 : best;
      if (style === 1) i = Math.max(0, i - 1);
      if (style === 2) i = Math.min(sorted.length - 1, i + 1);
      return sorted[i];
    }
    // no rider weights on the product: the Find my length rule (weight + how you ride), nearest length on sale
    const kind = this.#data && this.#data.type === 'Snowboard' ? 'board' : 'ski';
    const t = FIT().lengthTarget({ kind, kg, terrain: style === 1 ? 'park' : style === 2 ? 'powder' : 'all' });
    return t ? FIT().nearestLength(t.target, sorted) : null;
  }

  /** "in stock at Richmond Hill" / "in stock" / "sold out in this size". @param {string} value */
  #stockLine(value) {
    const t = this.t;
    const pos = Number(this.dataset.sizePos || 0);
    const list = (this.#data && this.#data.v) || [];
    const curId = currentVariantId();
    const cur = list.find((/** @type {any} */ v) => String(v.id) === curId);
    const v =
      list.find((/** @type {any} */ x) => x.o[pos - 1] === value && cur && x.o.every((/** @type {string} */ o, /** @type {number} */ k) => k === pos - 1 || o === cur.o[k])) ||
      list.find((/** @type {any} */ x) => x.o[pos - 1] === value);
    if (v && v.a && !stockKnown.has(String(v.id)) && (String(v.id) !== curId || pageUnpicked())) {
      fetchStock(v.id).then(() => {
        if (this.#rec === value && stockKnown.has(String(v.id))) this.#paintFinder(false);
      });
    }
    return stockWords(v, t);
  }

  #markRec() {
    const pos = Number(this.dataset.sizePos || 0);
    for (const box of this.querySelectorAll(`.bl-opt[data-pos="${pos}"] .opt`)) {
      const on = this.#rec != null && /** @type {HTMLElement} */ (box).dataset.value === this.#rec;
      box.classList.toggle('is-rec', on);
      if (on) box.setAttribute('data-rec', this.t.yourFit || 'Your fit');
      else box.removeAttribute('data-rec');
    }
  }
}

/** The stores with the shopper's first. @param {Array<{short: string, name: string, loc: string}>} stores */
function orderStores(stores) {
  const chosen = myStore();
  if (!chosen) return stores;
  const mine = stores.find((s) => s.short === chosen.short || s.name === chosen.name);
  return mine ? [mine, ...stores.filter((s) => s !== mine)] : stores;
}

/** Helmet size from a head measurement: ranges in the values ("M (55-59)") first, else S < 56 ≤ M < 60 ≤ L. */
/* ---------- gallery ---------- */

/**
 * @typedef {object} GalleryRefs
 * @property {HTMLElement} [slides]
 * @property {HTMLElement} [count]
 * @property {HTMLDialogElement} [lightbox]
 * @property {HTMLImageElement} [lbImg]
 * @property {HTMLElement} [lbCap]
 * @extends {Component<GalleryRefs>}
 */
class BlGallery extends Component {
  /** @type {number | 'specs'} */
  #index = 0;
  #lb = 0;
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKey(event), { signal });
    document.addEventListener('bl:wish', () => this.#paintWish(), { signal });
    window.addEventListener('storage', () => this.#paintWish(), { signal });
    this.#bindStrip(signal);
    this.#paintWish();
  }

  updatedCallback() {
    super.updatedCallback();
    /* re-rendered for another variant: keep the view (specs stays specs) */
    const slides = this.#slides();
    const media = slides.filter((s) => s.dataset.i !== 'specs');
    if (this.#index === 'specs' && !slides.some((s) => s.dataset.i === 'specs')) this.#index = 0;
    if (typeof this.#index === 'number' && this.#index >= media.length) this.#index = 0;
    this.show(this.#index, false);
    this.#paintWish();
    if (this.#controller) this.#bindStrip(this.#controller.signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    const dialog = this.querySelector('dialog.bl-lb');
    if (dialog) unlockScroll(dialog);
  }

  #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.bl-gal__slide')]);
  }

  /**
   * @param {number | 'specs'} index
   * @param {boolean} [scroll] - phones: scroll the strip to it
   */
  show(index, scroll = true) {
    const slides = this.#slides();
    if (!slides.length) return;
    const key = String(index);
    const target = slides.find((s) => s.dataset.i === key) || slides[0];
    this.#index = target.dataset.i === 'specs' ? 'specs' : Number(target.dataset.i || 0);
    for (const s of slides) s.classList.toggle('is-on', s === target);
    for (const thumb of this.querySelectorAll('.bl-gal__thumb')) {
      thumb.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (thumb).dataset.go === String(this.#index)));
    }
    const media = slides.filter((s) => s.dataset.i !== 'specs').length;
    const count = this.querySelector('.bl-gal__count');
    if (count) {
      count.textContent =
        this.#index === 'specs'
          ? `${this.dataset.specsLabel || 'Specs'}${this.dataset.len ? ' · ' + this.dataset.len : ''}`
          : `${Number(this.#index) + 1} / ${media}`;
    }
    this.classList.toggle('is-specs', this.#index === 'specs');
    if (scroll && phone.matches) {
      const strip = this.querySelector('.bl-gal__slides');
      if (strip instanceof HTMLElement) strip.scrollTo({ left: target.offsetLeft, behavior: behavior() });
    }
    this.#paintDots(slides.indexOf(target));
  }

  /** @param {number} at */
  #paintDots(at) {
    const dots = this.querySelectorAll('.bl-gal__dots i');
    dots.forEach((dot, k) => dot.classList.toggle('is-on', k === at));
  }

  /** @param {AbortSignal} signal */
  #bindStrip(signal) {
    const strip = this.querySelector('.bl-gal__slides');
    if (!(strip instanceof HTMLElement) || strip.dataset.blBound === '1') return;
    strip.dataset.blBound = '1';
    strip.addEventListener(
      'scroll',
      () => {
        if (!phone.matches) return;
        const at = Math.round(strip.scrollLeft / Math.max(1, strip.clientWidth));
        const slides = this.#slides();
        const slide = slides[at];
        this.#paintDots(at);
        this.classList.toggle('is-specs', !!slide && slide.dataset.i === 'specs');
      },
      { passive: true, signal }
    );
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const wish = target.closest('[data-bl-wish]');
    if (wish instanceof HTMLElement) {
      const list = /** @type {any} */ (window).BL && /** @type {any} */ (window).BL.wishlist;
      if (list) {
        const on = list.toggle({ h: wish.dataset.handle || '', t: wish.dataset.title || '', v: wish.dataset.vendor || '', u: wish.dataset.url || '', i: wish.dataset.image || '' });
        wish.setAttribute('aria-pressed', String(on));
      }
      return;
    }
    const go = target.closest('[data-go]');
    if (go instanceof HTMLElement) {
      const value = go.dataset.go === 'specs' ? 'specs' : Number(go.dataset.go);
      this.show(value, true);
      return;
    }
    const lbAction = target.closest('[data-lb]');
    if (lbAction instanceof HTMLElement) {
      if (lbAction.dataset.lb === 'close') this.closeLightbox();
      else this.#step(Number(lbAction.dataset.lb));
      return;
    }
    if (target === this.refs.lightbox) {
      this.closeLightbox();
      return;
    }
    const slide = target.closest('.bl-gal__slide');
    if (slide instanceof HTMLElement && slide.dataset.i !== 'specs' && !target.closest('.bl-lb')) {
      const opener = slide.querySelector('.bl-gal__open');
      this.#opener = opener instanceof HTMLElement ? opener : slide;
      this.openLightbox(Number(slide.dataset.i || 0));
    }
  }

  /** @param {KeyboardEvent} event */
  #onKey(event) {
    const dialog = this.refs.lightbox;
    if (dialog && dialog.open) {
      if (event.key === 'ArrowLeft') this.#step(-1);
      if (event.key === 'ArrowRight') this.#step(1);
      return;
    }
    const thumb = event.target instanceof Element ? event.target.closest('.bl-gal__thumb') : null;
    if (thumb && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      const thumbs = [...this.querySelectorAll('.bl-gal__thumb')];
      const at = thumbs.indexOf(thumb);
      const next = thumbs[(at + (event.key === 'ArrowRight' ? 1 : -1) + thumbs.length) % thumbs.length];
      if (next instanceof HTMLElement) {
        next.focus();
        next.click();
      }
    }
  }

  /** @param {number} index */
  openLightbox(index) {
    const dialog = this.refs.lightbox;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    this.#lb = index;
    this.#paintLightbox();
    /* the page under the viewer must not move (phones scroll the document itself): Horizon's shared lock */
    lockScroll(dialog);
    dialog.showModal();
    dialog.addEventListener('close', () => {
      unlockScroll(dialog);
      const opener = this.#opener || this.querySelector('.bl-gal__slide.is-on .bl-gal__open');
      this.#opener = null;
      focusBack(dialog, opener);
    }, { once: true });
  }

  closeLightbox() {
    const dialog = this.refs.lightbox;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  }

  /** @param {number} d */
  #step(d) {
    const media = this.#slides().filter((s) => s.dataset.i !== 'specs');
    if (!media.length) return;
    this.#lb = (this.#lb + d + media.length) % media.length;
    this.#paintLightbox();
  }

  #paintLightbox() {
    const media = this.#slides().filter((s) => s.dataset.i !== 'specs');
    const slide = media[this.#lb];
    const img = this.refs.lbImg;
    const cap = this.refs.lbCap;
    if (!slide || !(img instanceof HTMLImageElement)) return;
    img.src = slide.dataset.full || '';
    img.alt = slide.dataset.alt || '';
    if (cap) cap.textContent = `${this.#lb + 1} / ${media.length}${slide.dataset.alt ? ' · ' + slide.dataset.alt : ''}`;
  }

  #paintWish() {
    const button = this.querySelector('[data-bl-wish]');
    const list = /** @type {any} */ (window).BL && /** @type {any} */ (window).BL.wishlist;
    if (button instanceof HTMLElement && list) button.setAttribute('aria-pressed', String(list.has(button.dataset.handle || '')));
  }
}

/* ---------- common questions: the buy column's short FAQ, and the full list in the Questions band ---------- */

/**
 * Run an answer's action: the fit finder, the size chart (the finder when there is none), or Get it.
 * @param {string} kind - length | size | chart | getit
 */
function runFaqAction(kind) {
  const opts = document.querySelector('bl-options');
  const finder = document.querySelector('bl-options [data-bl-finder]');
  if (kind === 'chart') {
    const chart = document.querySelector('[data-bl-sizechart]');
    if (chart instanceof HTMLElement) {
      chart.click();
      return;
    }
  }
  if (kind === 'length' || kind === 'size' || kind === 'chart') {
    if (opts instanceof BlOptions && finder) opts.toggleFinder(true, true);
    return;
  }
  if (kind === 'getit') {
    const get = document.querySelector('.bl-get');
    if (!(get instanceof HTMLElement)) return;
    get.scrollIntoView({ behavior: behavior(), block: 'center' });
    get.classList.remove('is-flash');
    void get.offsetWidth;
    get.classList.add('is-flash');
  }
}

/** Which actions this page can run (a finder, a size chart, Get it). @param {string} kind */
function canRunFaqAction(kind) {
  if (kind === 'getit') return !!document.querySelector('.bl-get');
  if (kind === 'chart') return !!document.querySelector('[data-bl-sizechart], bl-options [data-bl-finder]');
  if (kind === 'length' || kind === 'size') return !!document.querySelector('bl-options [data-bl-finder]');
  return false;
}

class BlQa extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    for (const act of this.querySelectorAll('[data-bl-act]')) {
      if (act instanceof HTMLElement && !canRunFaqAction(act.dataset.blAct || '')) act.hidden = true;
    }
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const q = target ? target.closest('[data-qa]') : null;
      if (q instanceof HTMLElement) this.toggle(q);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      const q = event.target instanceof HTMLElement ? event.target.closest('[data-qa]') : null;
      if (!(q instanceof HTMLElement)) return;
      const all = [...this.querySelectorAll('[data-qa]')];
      const k = all.indexOf(q);
      let next = -1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (k + 1) % all.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (k - 1 + all.length) % all.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = all.length - 1;
      if (next < 0) return;
      event.preventDefault();
      const to = all[next];
      if (to instanceof HTMLElement) to.focus();
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** Open this question's answer (closing the open one); a second press closes it. @param {HTMLElement} q */
  toggle(q) {
    const open = q.getAttribute('aria-expanded') !== 'true';
    for (const other of this.querySelectorAll('[data-qa]')) {
      const on = other === q && open;
      other.setAttribute('aria-expanded', String(on));
      const panel = this.querySelector(`#${CSS.escape(other.getAttribute('aria-controls') || '')}`);
      if (panel instanceof HTMLElement) panel.hidden = !on;
    }
  }
}

/** The Questions band: the full list from the Common questions JSON (one source); hidden when there is none. */
function drawFaqMirror() {
  const data = readJSON(document.querySelector('script[data-bl-faq-all]'));
  for (const box of document.querySelectorAll('bl-faq[data-bl-faq-mirror]')) {
    if (!(box instanceof HTMLElement)) continue;
    const list = box.querySelector('.bl-faq__list');
    const items = data && Array.isArray(data.items) ? data.items : null;
    if (items && list) {
      const words = (data && data.words) || {};
      list.innerHTML = items
        .map((/** @type {any} */ item) => {
          const act = item.act && canRunFaqAction(item.act) && words[item.act] ? `<button type="button" class="text-link bl-qa__act" data-bl-act="${escHTML(item.act)}">${escHTML(words[item.act])} →</button>` : '';
          return `<details class="bl-faq__item"><summary>${escHTML(item.q)}</summary><p>${escHTML(item.a).split('\n').join('<br>')}</p>${act}</details>`;
        })
        .join('');
    }
    const has = !!(list && list.querySelector('.bl-faq__item'));
    box.hidden = !has;
    const band = box.closest('.bl-band');
    if (band instanceof HTMLElement) band.hidden = !has;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const act = target ? target.closest('[data-bl-act]') : null;
  if (act instanceof HTMLElement) runFaqAction(act.dataset.blAct || '');
});

/* ---------- complete the setup ---------- */

class BlSetup extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {MouseEvent} event */
  async #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const one = target.closest('[data-bl-set-add]');
    if (one instanceof HTMLButtonElement) {
      one.disabled = true;
      const ok = await addToCart([{ id: Number(one.dataset.blSetAdd), quantity: 1 }], one);
      one.disabled = false;
      this.#say(ok);
      if (ok) {
        one.classList.add('is-done');
        setTimeout(() => one.classList.remove('is-done'), 1400);
      }
      return;
    }
    const all = target.closest('[data-bl-set-all]');
    if (all instanceof HTMLButtonElement) {
      const root = document.querySelector('bl-pdp');
      if (root instanceof BlPdp && root.unpicked) {
        root.flashSize();
        return;
      }
      const ids = [...this.querySelectorAll('[data-bl-set-add]')].map((b) => Number(/** @type {HTMLElement} */ (b).dataset.blSetAdd));
      const own = Number(currentVariantId());
      const items = [...(own ? [{ id: own, quantity: 1 }] : []), ...ids.map((id) => ({ id, quantity: 1 }))];
      if (!items.length) return;
      all.disabled = true;
      const ok = await addToCart(items, all);
      all.disabled = false;
      this.#say(ok);
    }
  }

  /** @param {boolean} ok */
  #say(ok) {
    const msg = this.querySelector('.bl-set__msg');
    if (msg) msg.textContent = ok ? this.dataset.added || '' : this.dataset.error || '';
  }
}

/* ---------- document-wide: the specs table heads, the questions, the lookbook tag ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const head = target.closest('[data-bl-choose]');
  if (head instanceof HTMLElement) {
    const opts = document.querySelector('bl-options');
    if (opts instanceof BlOptions) opts.chooseAt(Number(head.dataset.optionPosition), head.dataset.value || '');
  }
});

/* R15: the All lengths rows are focusable (tabindex 0): Enter / Space choose, as a click does */
document.addEventListener('keydown', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || (event.key !== 'Enter' && event.key !== ' ')) return;
  const row = target.closest('[data-bl-choose]');
  if (!(row instanceof HTMLElement) || row instanceof HTMLButtonElement || row !== target) return;
  event.preventDefault();
  const opts = document.querySelector('bl-options');
  if (opts instanceof BlOptions) opts.chooseAt(Number(row.dataset.optionPosition), row.dataset.value || '');
});

/* R15: while a new variant loads, the key figures that differ between lengths dip to 25 % (160 ms, bl-pdp.css); the
   re-rendered block brings them back (morph drops the class). Nothing moves under reduced motion. */
let swapTimer = 0;
document.addEventListener(SELECT_EVENT, (event) => {
  if (reducedMotion.matches || !(event.target instanceof Element) || !event.target.closest('bl-pdp')) return;
  const figures = document.querySelectorAll('.bl-sp2 .bl-kf[data-vary]');
  if (!figures.length) return;
  for (const figure of figures) figure.classList.add('is-swap');
  clearTimeout(swapTimer);
  swapTimer = window.setTimeout(() => {
    for (const figure of document.querySelectorAll('.bl-sp2 .bl-kf.is-swap')) figure.classList.remove('is-swap');
  }, 1500);
});
document.addEventListener('bl:pdp:synced', () => {
  for (const figure of document.querySelectorAll('.bl-sp2 .bl-kf.is-swap')) figure.classList.remove('is-swap');
});

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-bl-choose-select]')) return;
  const opts = document.querySelector('bl-options');
  if (opts instanceof BlOptions) opts.chooseAt(Number(select.dataset.optionPosition), select.value);
});

document.addEventListener(
  'toggle',
  (event) => {
    const item = event.target;
    if (!(item instanceof HTMLDetailsElement) || !item.open || !item.classList.contains('bl-faq__item')) return;
    const list = item.closest('.bl-faq__list');
    for (const other of list ? list.querySelectorAll('details[open]') : []) if (other !== item) /** @type {HTMLDetailsElement} */ (other).open = false;
  },
  true
);

/** The lookbook on a product page: a piece that is this product says "This item" instead of "+". */
function tagSelf() {
  for (const looks of document.querySelectorAll('.bl-looks[data-self-url]')) {
    const self = /** @type {HTMLElement} */ (looks).dataset.selfUrl || '';
    const label = /** @type {HTMLElement} */ (looks).dataset.selfLabel || 'This item';
    if (!self) continue;
    for (const row of looks.querySelectorAll('.bll-pc')) {
      const link = row.querySelector('a.bll-pc__name[href]');
      if (!(link instanceof HTMLAnchorElement)) continue;
      const path = new URL(link.href, location.origin).pathname.replace(/\/$/, '');
      const isSelf = path.endsWith(self.replace(/\/$/, ''));
      row.classList.toggle('is-self', isSelf);
      const ph = row.querySelector('.bll-pc__ph');
      const tag = row.querySelector('.bll-pc__self');
      if (isSelf && ph && !tag) {
        const badge = document.createElement('span');
        badge.className = 'badge bll-pc__self';
        badge.textContent = label;
        ph.append(badge);
      }
      if (!isSelf && tag) tag.remove();
    }
  }
}
/** Long texts (the description in Why / About): clamp with "Read more" when they run past the set lines. */
function clampTexts() {
  for (const text of document.querySelectorAll('[data-bl-clamp]')) {
    if (!(text instanceof HTMLElement) || text.dataset.blClampReady) continue;
    const button = text.parentElement ? text.parentElement.querySelector('[data-bl-more]') : null;
    if (!(button instanceof HTMLElement)) continue;
    text.dataset.blClampReady = '1';
    text.classList.add('is-clamped');
    if (text.scrollHeight <= text.clientHeight + 24) {
      text.classList.remove('is-clamped');
      continue;
    }
    button.hidden = false;
    button.addEventListener('click', () => {
      const open = text.classList.toggle('is-clamped') === false;
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? button.dataset.less || '' : button.dataset.more || '';
    });
  }
}

/* the Personality video (snippets/bl-pdp-media.liquid): a YouTube / Vimeo poster loads its player only when pressed;
   a muted looping MP4 holds still (with controls) for anyone who asked for less motion */
document.addEventListener('click', (event) => {
  const play = event.target instanceof Element ? event.target.closest('[data-bl-embed]') : null;
  if (!(play instanceof HTMLElement)) return;
  const frame = document.createElement('iframe');
  frame.src = play.dataset.blEmbed || '';
  frame.title = play.dataset.title || '';
  frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  frame.allowFullscreen = true;
  play.replaceWith(frame);
});

/* the embed's poster: the video's own thumbnail (YouTube maxresdefault → hqdefault; Vimeo oEmbed thumbnail_url);
   the scene image / brand hero underneath shows only while there is none */
function videoThumbs() {
  for (const img of document.querySelectorAll('img[data-yt-thumb]')) {
    if (!(img instanceof HTMLImageElement) || img.dataset.ready) continue;
    img.dataset.ready = '1';
    const id = img.dataset.ytThumb || '';
    const show = () => {
      // YouTube answers a missing maxresdefault with a 120 px placeholder
      if (img.naturalWidth > 120) {
        img.hidden = false;
        img.closest('.bl-media__play')?.classList.add('has-thumb');
      } else if (!img.src.includes('hqdefault')) {
        img.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      }
    };
    img.addEventListener('load', show);
    img.addEventListener('error', () => {
      if (!img.src.includes('hqdefault')) img.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    });
    img.loading = 'eager'; // a hidden lazy image would never load
    img.src = `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
  }
  for (const img of document.querySelectorAll('img[data-vimeo-thumb]')) {
    if (!(img instanceof HTMLImageElement) || img.dataset.ready) continue;
    img.dataset.ready = '1';
    const id = img.dataset.vimeoThumb || '';
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${id}`)}&width=1280`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || !data.thumbnail_url) return;
        img.addEventListener('load', () => {
          img.hidden = false;
          img.closest('.bl-media__play')?.classList.add('has-thumb');
        });
        img.loading = 'eager';
        img.src = data.thumbnail_url;
      })
      .catch(() => {});
  }
}

function calmVideos() {
  if (!reducedMotion.matches) return;
  for (const video of document.querySelectorAll('.bl-media__vid')) {
    if (!(video instanceof HTMLVideoElement)) continue;
    video.removeAttribute('autoplay');
    video.pause();
    video.controls = true;
  }
}

function onReady() {
  tagSelf();
  clampTexts();
  drawFaqMirror();
  calmVideos();
  videoThumbs();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady, { once: true });
else onReady();

if (!customElements.get('bl-pdp')) customElements.define('bl-pdp', BlPdp);
if (!customElements.get('bl-options')) customElements.define('bl-options', BlOptions);
if (!customElements.get('bl-gallery')) customElements.define('bl-gallery', BlGallery);
if (!customElements.get('bl-qa')) customElements.define('bl-qa', BlQa);
if (!customElements.get('bl-setup')) customElements.define('bl-setup', BlSetup);
